/**
 * Search page container combining thematic and regional filters.
 */
import React, {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SearchFocusTarget, User } from "@/App";
import { useApp } from "@/contexts/AppContext";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import {
  Search as SearchIcon,
  Filter,
  ExternalLink,
  Info,
  ChevronRight,
  ChevronLeft,
  ChevronsDown,
  ChevronsUp,
  Eye,
  Globe,
  MapPin,
  X,
} from "lucide-react";
import { HintBanner } from "./search-page/HintBanner";
import { RegionalFilterSection } from "./search-page/RegionalFilterSection";
import {
  ThematicFilterSection,
  type ThematicCategoryNode,
} from "./search-page/ThematicFilterSection";
import { WebsiteDetailDrawer } from "@/components/website-details/WebsiteDetailDrawer";
import { Skeleton } from "./ui/skeleton";

import {
  fetchThematicCategoryTree,
  fetchRegionCounts,
  fetchThematicCategoryCounts,
  searchWebsitesPaged,
  type ThematicCategoryCountNode,
  type ThematicCategoryDto,
  type WebsiteSearchResult,
} from "@/services/searchPage.service";
import { recordPageview } from "@/services/website.service";
import { toUiScaleFactor } from "@/utils/uiScale";
import {
  getPostalCodeDisplayLabel,
  parseRegionSelectionKey,
  type MapLayerId,
} from "./search-page/region-filter-section/mapHelpers";
import { MAX_CATEGORY_PATH_SEGMENTS } from "@/constants/category";
import { getPublicRegionCatalog } from "@/services/region.service";
import type { NavigateOptions } from "@/app/types";

const buildCategoryKey = (path: string[]) => path.join("/");
const TABLET_LAYOUT_BREAKPOINT = 1024;
const MIN_PAGINATION_BUTTONS = 3;
const MAX_PAGINATION_BUTTONS = 11;
const PAGINATION_OUTER_GAP_PX = 8;
const PAGINATION_INNER_GAP_PX = 4;
const SORT_BY_VALUES = new Set([
  "relevance",
  "date-new",
  "date-old",
  "title-asc",
  "title-desc",
]);
const DATE_FILTER_VALUES = new Set(["all", "week", "month", "year"]);
const PER_PAGE_VALUES = new Set(["10", "25", "50"]);
const DEFAULT_SORT_BY = "relevance";
const DEFAULT_DATE_FILTER = "all";
const DEFAULT_PER_PAGE = "10";
const MAX_SEARCH_QUERY_LENGTH = 200;
const MAX_URL_REGIONS = 100;
const REGION_LAYER_PRIORITY: Record<MapLayerId, number> = {
  federalStates: 0,
  administrativeDistricts: 1,
  districts: 2,
  postalCodes: 3,
};

type SearchPageUrlState = {
  query: string;
  queryDe: string;
  sortBy: string;
  dateFilter: string;
  page: number;
  size: string;
  regions: string[];
  categorySlugs: string[];
};

const clampUiPage = (value: unknown) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return 1;
  }
  return Math.max(1, Math.floor(numeric));
};

const normalizeQueryParam = (value: string | null) => {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.slice(0, MAX_SEARCH_QUERY_LENGTH);
};

const normalizeCategorySlug = (value: string) => {
  const slug = value.trim().toLowerCase();
  return /^[a-z0-9-]+$/.test(slug) ? slug : null;
};

const normalizeRegionKeys = (regionKeys: string[]): string[] => {
  const normalized = Array.from(
    new Set(
      regionKeys
        .map((region) => region?.trim())
        .filter((region): region is string => !!region)
        .map((region) => {
          const parsed = parseRegionSelectionKey(region);
          return parsed ? region : null;
        })
        .filter((region): region is string => !!region)
        .slice(0, MAX_URL_REGIONS)
    )
  );
  normalized.sort((left, right) => left.localeCompare(right));
  return normalized;
};

const regionKeySignature = (regionKeys: string[]) => regionKeys.join("|");

const constrainRegionKeysToMapContext = (
  regionKeys: string[],
  layer: MapLayerId,
  activeState: string | null
): string[] => {
  const normalized = normalizeRegionKeys(regionKeys);
  if (!activeState || layer === "federalStates") {
    return normalized;
  }

  const maxPriority = REGION_LAYER_PRIORITY[layer];
  const constrained = normalized.filter((regionKey) => {
    const parsed = parseRegionSelectionKey(regionKey);
    if (!parsed) {
      return false;
    }
    if (REGION_LAYER_PRIORITY[parsed.layerId] > maxPriority) {
      return false;
    }
    return parsed.state === activeState;
  });

  return normalizeRegionKeys(constrained);
};

const parseSearchPageUrlState = (search: string): SearchPageUrlState => {
  const params = new URLSearchParams(search);
  const rawSortBy = (params.get("sortBy") ?? "").trim().toLowerCase();
  const rawDateFilter = (params.get("dateFilter") ?? "").trim().toLowerCase();
  const rawSize = (params.get("size") ?? "").trim();

  const regions = normalizeRegionKeys(params.getAll("regions"));

  const categoryPathRaw = (params.get("categories") ?? "").trim();
  const categorySlugs = categoryPathRaw
    ? categoryPathRaw
        .split("/")
        .map((slug) => normalizeCategorySlug(slug))
        .filter((slug): slug is string => !!slug)
        .slice(0, MAX_CATEGORY_PATH_SEGMENTS)
    : [];

  return {
    query: normalizeQueryParam(params.get("query")),
    queryDe: normalizeQueryParam(params.get("queryDe")),
    sortBy: SORT_BY_VALUES.has(rawSortBy) ? rawSortBy : DEFAULT_SORT_BY,
    dateFilter: DATE_FILTER_VALUES.has(rawDateFilter)
      ? rawDateFilter
      : DEFAULT_DATE_FILTER,
    page: clampUiPage(params.get("page")),
    size: PER_PAGE_VALUES.has(rawSize) ? rawSize : DEFAULT_PER_PAGE,
    regions,
    categorySlugs,
  };
};

const isReloadNavigation = () => {
  if (typeof window === "undefined" || typeof window.performance === "undefined") {
    return false;
  }

  const navigationEntries = window.performance.getEntriesByType?.("navigation");
  const navigationEntry = navigationEntries?.[0] as
    | PerformanceNavigationTiming
    | undefined;
  if (navigationEntry && navigationEntry.type) {
    return navigationEntry.type === "reload";
  }

  const legacyNavigation = (window.performance as Performance & {
    navigation?: { type?: number };
  }).navigation;
  return legacyNavigation?.type === 1;
};

const readHistoryStateSearchQuery = () => {
  if (typeof window === "undefined") {
    return "";
  }
  const state = window.history.state as { searchQuery?: unknown } | null;
  return typeof state?.searchQuery === "string" ? state.searchQuery : "";
};

const hasIncomingSearchState = (
  state: SearchPageUrlState,
  initialQuery: string,
  historyQuery: string
) =>
  Boolean(
    state.query ||
      state.queryDe ||
      initialQuery ||
      historyQuery ||
      state.regions.length > 0 ||
      state.categorySlugs.length > 0
  );

const clearSearchSelectionsForReload = (
  state: SearchPageUrlState
): SearchPageUrlState => ({
  ...state,
  query: "",
  queryDe: "",
  page: 1,
  regions: [],
  categorySlugs: [],
});

const buildSearchPageQueryString = (state: SearchPageUrlState) => {
  const params = new URLSearchParams();
  if (state.query) {
    params.set("query", state.query);
  }
  if (state.queryDe) {
    params.set("queryDe", state.queryDe);
  }
  if (state.page > 1) {
    params.set("page", String(state.page));
  }
  if (state.size !== DEFAULT_PER_PAGE) {
    params.set("size", state.size);
  }
  if (state.sortBy !== DEFAULT_SORT_BY) {
    params.set("sortBy", state.sortBy);
  }
  if (state.dateFilter !== DEFAULT_DATE_FILTER) {
    params.set("dateFilter", state.dateFilter);
  }
  state.regions.forEach((region) => params.append("regions", region));
  if (state.categorySlugs.length > 0) {
    params.set("categories", state.categorySlugs.join("/"));
  }
  return params.toString();
};

const isRequestCanceled = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const candidate = error as { code?: string; name?: string };
  return (
    candidate.code === "ERR_CANCELED" ||
    candidate.name === "CanceledError" ||
    candidate.name === "AbortError"
  );
};

const pickLocalizedName = (node: ThematicCategoryDto, language: string) => {
  const lower = (language || "").toLowerCase();
  const isGerman = lower.startsWith("de");
  return isGerman ? node.nameDe : node.nameEn;
};

const localizeThematicTree = (
  nodes: ThematicCategoryDto[],
  counts: Record<string, number>,
  language: string,
  trail: string[] = []
): ThematicCategoryNode[] =>
  nodes.map((node) => {
    const nextTrail = [...trail, node.slug];
    const key = buildCategoryKey(nextTrail);
    const children = node.children
      ? localizeThematicTree(node.children, counts, language, nextTrail)
      : [];
    const childSum = children.reduce(
      (sum, child) => sum + (child.count ?? 0),
      0
    );
    // Slug-based keys avoid locale-sensitive names; aggregate when backend omits parent counts.
    const explicitCount = counts[key] ?? counts[node.slug] ?? undefined;
    const count = explicitCount ?? childSum;

    return {
      slug: node.slug,
      name: pickLocalizedName(node, language),
      nameEn: node.nameEn,
      nameDe: node.nameDe,
      iconName: node.iconName ?? null,
      count,
      subcategories: children.length ? children : undefined,
    };
  });

const pruneThematicTree = (
  nodes: ThematicCategoryNode[]
): ThematicCategoryNode[] =>
  nodes.flatMap((node) => {
    const children = node.subcategories
      ? pruneThematicTree(node.subcategories)
      : [];
    const count = node.count ?? 0;
    if (count <= 0 && children.length === 0) {
      return [];
    }
    return [
      {
        ...node,
        subcategories: children.length ? children : undefined,
      },
    ];
  });

const flattenThematicCounts = (
  nodes: ThematicCategoryCountNode[],
  trail: string[] = [],
  acc: Record<string, number> = {}
): Record<string, number> => {
  nodes.forEach((node) => {
    const nextTrail = [...trail, node.slug];
    const key = buildCategoryKey(nextTrail);
    acc[key] = node.count ?? 0;
    if (node.children?.length) {
      flattenThematicCounts(node.children, nextTrail, acc);
    }
  });
  return acc;
};

const normalizeSelectedCategoryPath = (
  categories: ThematicCategoryNode[],
  slugs: string[]
): string[] => {
  if (slugs.length === 0) return [];
  const normalized: string[] = [];
  let current = categories;
  for (const slug of slugs) {
    const next = current.find((cat) => cat.slug === slug);
    if (!next) break;
    normalized.push(slug);
    current = next.subcategories ?? [];
  }
  return normalized;
};

const findCategoryBySlugs = (
  categories: ThematicCategoryNode[],
  slugs: string[]
): ThematicCategoryNode | null => {
  if (slugs.length === 0) return null;
  let currentList = categories;
  let current: ThematicCategoryNode | undefined;

  for (const slug of slugs) {
    current = currentList.find((cat) => cat.slug === slug);
    if (!current) break;
    currentList = current.subcategories ?? [];
  }
  return current ?? null;
};

const buildCategoryDisplayPath = (
  categories: ThematicCategoryNode[],
  slugs: string[]
): string[] => {
  const names: string[] = [];
  let level = categories;
  slugs.forEach((slug) => {
    const node = level.find((cat) => cat.slug === slug);
    if (!node) return;
    names.push(node.name);
    level = node.subcategories ?? [];
  });
  return names;
};

interface SearchPageProps {
  navigate: (page: string, options?: NavigateOptions) => void;
  currentUser: User | null;
  onLogout: () => void;
  onSearch: (query: string) => void;
  initialQuery?: string;
  onNavigateToSearchPage: (target: SearchFocusTarget) => void;
  focusTarget?: SearchFocusTarget | null;
  onFocusHandled?: () => void;
  pageKey: SearchFocusTarget;
}

export function SearchPage({
  navigate,
  currentUser,
  onLogout,
  onSearch,
  initialQuery = "",
  onNavigateToSearchPage,
  focusTarget,
  onFocusHandled,
  pageKey,
}: SearchPageProps) {
  //Atention! This part is modified to solve the share query problem between Hompage and Search Page.
  const {
    t,
    language,
    searchQuery: sharedQuery,
    setSearchQuery: setSharedQuery,
    uiScale,
    activeTheme,
  } = useApp() as any;
  const isDark = activeTheme?.tone === "dark";
  const primaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 77%, var(--surface))",
  } as const;
  const secondaryTextStyle = {
    color: "color-mix(in srgb, var(--foreground) 66%, var(--surface))",
  } as const;
  const iconStyle = {
    color:
      "color-mix(in srgb, color-mix(in srgb, var(--primary) 70%, var(--accent)) 90%, var(--foreground))",
  } as const;
  const errorTextStyle = isDark
    ? { color: "color-mix(in srgb, var(--destructive) 88%, var(--foreground))" }
    : undefined;
  const searchLinkStyle = {
    "--search-link": isDark
      ? "color-mix(in srgb, var(--primary) 90%, var(--foreground))"
      : "var(--primary)",
    "--search-link-hover": isDark
      ? "color-mix(in srgb, var(--primary) 72%, var(--foreground))"
      : "color-mix(in srgb, var(--primary) 85%, var(--foreground))",
  } as CSSProperties;

  // Attention! Lang is changed to language. This part is modified to solve the share query problem between Hompage and Search Page.
  const locale =
    (language as string) ||
    (typeof navigator !== "undefined" ? navigator.language : "de-DE");
  const normalizedInitialQuery = normalizeQueryParam(initialQuery ?? "");
  const historyStateSearchQuery = useMemo(
    () => normalizeQueryParam(readHistoryStateSearchQuery()),
    []
  );
  const shouldClearSearchSelectionsOnReload = useMemo(
    () => isReloadNavigation(),
    []
  );
  const clearSearchSelectionsOnReloadRef = useRef(
    shouldClearSearchSelectionsOnReload
  );
  const initialUrlState = useMemo(
    () => {
      const parsed = parseSearchPageUrlState(
        typeof window !== "undefined" ? window.location.search : ""
      );
      const hasIncomingSearch = hasIncomingSearchState(
        parsed,
        normalizedInitialQuery,
        historyStateSearchQuery
      );
      return shouldClearSearchSelectionsOnReload && !hasIncomingSearch
        ? clearSearchSelectionsForReload(parsed)
        : parsed;
    },
    [
      shouldClearSearchSelectionsOnReload,
      normalizedInitialQuery,
      historyStateSearchQuery,
    ]
  );
  const effectiveInitialQuery =
    initialUrlState.query || normalizedInitialQuery || historyStateSearchQuery;

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(locale || "de-DE"),
    [locale]
  );

  const [viewMode, setViewMode] = useState<"categories" | "results">(
    "categories"
  );
  const [selectedCategorySlugs, setSelectedCategorySlugs] = useState<string[]>(
    initialUrlState.categorySlugs
  );
  const [selectedRegions, setSelectedRegions] = useState<string[]>(
    initialUrlState.regions
  );
  const [regionCatalogLabels, setRegionCatalogLabels] = useState<
    Record<string, string>
  >({});
  const [mapLayer, setMapLayer] = useState<MapLayerId>("federalStates");
  const [mapActiveState, setMapActiveState] = useState<string | null>(null);
  const [regionCounts, setRegionCounts] = useState<Map<string, number>>(
    () => new Map()
  );
  const [rawThematicCategories, setRawThematicCategories] = useState<
    ThematicCategoryDto[]
  >([]);
  const [thematicTreeLoading, setThematicTreeLoading] = useState<boolean>(true);
  const [thematicCountsLoading, setThematicCountsLoading] =
    useState<boolean>(false);
  const [hasLoadedThematicCountsOnce, setHasLoadedThematicCountsOnce] =
    useState<boolean>(false);
  const [categoryCountsMap, setCategoryCountsMap] = useState<
    Record<string, number>
  >({});

  useEffect(() => {
    let cancelled = false;
    getPublicRegionCatalog({ includePostal: true, language })
      .then((rows) => {
        if (cancelled) return;
        const labels: Record<string, string> = {};
        rows.forEach((row) => {
          if (row.regionKey && row.displayLabel) {
            labels[row.regionKey] = row.displayLabel;
          }
        });
        setRegionCatalogLabels(labels);
      })
      .catch(() => {
        if (cancelled) return;
        setRegionCatalogLabels({});
      });
    return () => {
      cancelled = true;
    };
  }, [language]);

  const resultsFilterRef = useRef<HTMLDivElement | null>(null);
  const categoryHintRef = useRef<HTMLDivElement | null>(null);
  const resultsHintRef = useRef<HTMLDivElement | null>(null);
  const regionalFilterRef = useRef<HTMLDivElement | null>(null);
  const [thematicSectionMaxHeightPx, setThematicSectionMaxHeightPx] = useState<
    number | null
  >(null);

  const filterInputRef = useRef<HTMLInputElement | null>(null);
  const filterInputFocusedRef = useRef(false);

  const formatResultsSummary = useCallback(
    (start: number, end: number, count: number) => {
      const template =
        t("categories.resultsSummary") || "{{start}}-{{end}} / {{count}}";
      const startStr = numberFormatter.format(start);
      const endStr = numberFormatter.format(end);
      const countStr = numberFormatter.format(count);

      return template
        .replace(/\{\{start\}\}/g, startStr)
        .replace(/\{\{end\}\}/g, endStr)
        .replace(/\{\{count\}\}/g, countStr);
    },
    [numberFormatter, t]
  );

  const thematicCategoriesFull = useMemo(
    () =>
      localizeThematicTree(
        rawThematicCategories,
        categoryCountsMap,
        language as string
      ),
    [rawThematicCategories, categoryCountsMap, language]
  );

  const thematicCategories = useMemo(
    () => pruneThematicTree(thematicCategoriesFull),
    [thematicCategoriesFull]
  );

  const categoryPath = useMemo(
    () => buildCategoryDisplayPath(thematicCategoriesFull, selectedCategorySlugs),
    [thematicCategoriesFull, selectedCategorySlugs]
  );

  const currentCategory = useMemo(
    () => findCategoryBySlugs(thematicCategories, selectedCategorySlugs),
    [thematicCategories, selectedCategorySlugs]
  );

  const isLeafCategory =
    !!currentCategory && !(currentCategory.subcategories?.length ?? 0);

  // Sorting/filter inputs for results section
  const [sortBy, setSortBy] = useState(initialUrlState.sortBy);
  const [filterDate, setFilterDate] = useState(initialUrlState.dateFilter);

  // split user input and actual search query
  // const [searchInCat, setSearchInCat] = useState(initialQuery);
  // const [searchQuery, setSearchQuery] = useState<string | null>(
  //   initialQuery.trim() ? initialQuery.trim() : null
  // );
  const [committedQuery, setCommittedQuery] = useState<string | null>(
    effectiveInitialQuery ? effectiveInitialQuery : null
  );
  const [committedQueryDe, setCommittedQueryDe] = useState<string | null>(
    initialUrlState.queryDe ? initialUrlState.queryDe : null
  );

  // Forces re-search even when query text is unchanged (user hits Enter twice)
  const [searchSeq, setSearchSeq] = useState(0);
  const [resolvedResultsKey, setResolvedResultsKey] = useState("");
  const resultsSyncKey = useMemo(
    () =>
      [
        committedQuery ?? "",
        committedQueryDe ?? "",
        filterDate,
        String(searchSeq),
        selectedRegions.join(","),
        selectedCategorySlugs.join("/"),
      ].join("|"),
    [committedQuery, committedQueryDe, filterDate, searchSeq, selectedRegions, selectedCategorySlugs]
  );
  // Validation error message for input.
  const [searchError, setSearchError] = useState<string | null>(null);

  // keep both fields in sync when initialQuery comes from outside
  // useEffect(() => {
  //   const trimmed = initialQuery.trim();
  //   setSearchInCat(initialQuery);
  //   setSearchQuery(trimmed ? trimmed : null);
  //   setViewMode(trimmed ? "results" : "categories");
  // }, [initialQuery]);
  useEffect(() => {
    const parsedState = parseSearchPageUrlState(
      typeof window !== "undefined" ? window.location.search : ""
    );
    const historyQuery = normalizeQueryParam(readHistoryStateSearchQuery());
    const hasIncomingSearch = hasIncomingSearchState(
      parsedState,
      normalizedInitialQuery,
      historyQuery
    );
    const reloadClear =
      clearSearchSelectionsOnReloadRef.current && !hasIncomingSearch;
    const urlState = reloadClear
      ? clearSearchSelectionsForReload(parsedState)
      : parsedState;
    if (reloadClear) {
      clearSearchSelectionsOnReloadRef.current = false;
    } else if (clearSearchSelectionsOnReloadRef.current) {
      clearSearchSelectionsOnReloadRef.current = false;
    }

    const nextQuery = reloadClear
      ? ""
      : urlState.query || normalizedInitialQuery || historyQuery;
    const nextQueryDe =
      !reloadClear && nextQuery ? urlState.queryDe || "" : "";
    const nextInput = reloadClear
      ? ""
      : urlState.query || (initialQuery ?? "").trim() || historyQuery;
    setSharedQuery(nextInput);
    setCommittedQuery(nextQuery ? nextQuery : null);
    setCommittedQueryDe(nextQueryDe ? nextQueryDe : null);
    setSortBy(urlState.sortBy);
    setFilterDate(urlState.dateFilter);
    const normalizedRegions = normalizeRegionKeys(urlState.regions);
    setSelectedRegions(normalizedRegions);
    setSelectedCategorySlugs(urlState.categorySlugs);
    setRegionalPage(urlState.page);
    setThematicPage(urlState.page);
    setRegionalPerPage(urlState.size);
    setThematicPerPage(urlState.size);
    setViewMode("categories");
  }, [initialQuery, normalizedInitialQuery, setSharedQuery]);

  useEffect(() => {
    let active = true;
    setThematicTreeLoading(true);
    fetchThematicCategoryTree()
      .then((tree) => {
        if (!active) return;
        setRawThematicCategories(tree || []);
      })
      .catch((err: unknown) => {
        if (!active) return;
        console.error("Failed to load thematic categories", err);
        setRawThematicCategories([]);
      })
      .finally(() => {
        if (!active) return;
        setThematicTreeLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (thematicTreeLoading) {
      return;
    }
    const normalized = normalizeSelectedCategoryPath(
      thematicCategoriesFull,
      selectedCategorySlugs
    );
    const isSame =
      normalized.length === selectedCategorySlugs.length &&
      normalized.every((slug, idx) => slug === selectedCategorySlugs[idx]);
    if (!isSame) {
      setSelectedCategorySlugs(normalized);
    }
  }, [selectedCategorySlugs, thematicCategoriesFull, thematicTreeLoading]);

  const scrollToCategoryHint = useCallback(() => {
    categoryHintRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    categoryHintRef.current?.focus?.({ preventScroll: true });
  }, []);

  const scrollToResultsHint = useCallback(() => {
    resultsHintRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    resultsHintRef.current?.focus?.({ preventScroll: true });
    resultsFilterRef.current?.focus?.({ preventScroll: true });
  }, []);

  const focusCategoriesSection = useCallback(() => {
    setViewMode("categories");
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => scrollToCategoryHint());
    } else {
      scrollToCategoryHint();
    }
  }, [scrollToCategoryHint]);

  const focusSearchSection = useCallback(() => {
    if (typeof window !== "undefined") {
      window.requestAnimationFrame(() => scrollToResultsHint());
    } else {
      scrollToResultsHint();
    }
  }, [scrollToResultsHint]);

  useEffect(() => {
    if (!initialQuery.trim()) return;
    scrollToResultsHint();
  }, [initialQuery, scrollToResultsHint]);

  useEffect(() => {
    if (!focusTarget) return;
    if (focusTarget === "categories") {
      focusCategoriesSection();
    } else {
      focusSearchSection();
    }
    onFocusHandled?.();
  }, [focusTarget, focusCategoriesSection, focusSearchSection, onFocusHandled]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!filterInputFocusedRef.current) return;
    const input = filterInputRef.current;
    if (!input || document.activeElement === input) return;
    input.focus({ preventScroll: true });
    //}, [searchInCat]);
  }, [sharedQuery]);

  // submit <=200.
  const handleSubmitSearch = useCallback(() => {
    const trimmed = (sharedQuery ?? "").trim();
    if (trimmed.length > MAX_SEARCH_QUERY_LENGTH) {
      setSearchError(
        t("common.searchTooLong") ||
          "Search term must be at most 200 characters."
      );
      filterInputRef.current?.focus({ preventScroll: true });
      return;
    }

    setSearchError(null);

    if (!trimmed) {
      // empty query show recent 50
      setCommittedQuery(null);
      setCommittedQueryDe(null);
      setRegionalPage(1);
      setThematicPage(1);
      setSearchSeq((x) => x + 1);
      setViewMode("categories");
      scrollToResultsHint();
      return;
    }

    // non-empty query do search
    setCommittedQuery(trimmed);
    setCommittedQueryDe(null);
    setRegionalPage(1);
    setThematicPage(1);
    setSearchSeq((x) => x + 1);
    setViewMode("categories");
    scrollToResultsHint();
  }, [
    sharedQuery,
    scrollToResultsHint,
    t,
  ]);

  // --- Regional results pagination ---
  const [regionalPage, setRegionalPage] = useState(initialUrlState.page);
  const [regionalPerPage, setRegionalPerPage] = useState(initialUrlState.size);

  // --- Thematic results pagination ---
  const [thematicPage, setThematicPage] = useState(initialUrlState.page);
  const [thematicPerPage, setThematicPerPage] = useState(initialUrlState.size);

  // --- Backend search results ---
  const [results, setResults] = useState<WebsiteSearchResult[]>([]);
  const [resultsTotalElements, setResultsTotalElements] = useState(0);
  const [resultsTotalPages, setResultsTotalPages] = useState(0);
  const [loadingResults, setLoadingResults] = useState(false);
  const [resultsError, setResultsError] = useState<string | null>(null);
  const resultsAutoRetryRef = useRef<Map<string, number>>(new Map());
  // Website detail popup state (detail + similar fetched in a single request).
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailWebsiteId, setDetailWebsiteId] = useState<string | null>(null);

  const openDetail = useCallback((id: string) => {
    setDetailWebsiteId(id);
    setDetailOpen(true);
  }, []);

  useEffect(() => {
    let active = true;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    const controller = new AbortController();
    setLoadingResults(true);
    setThematicCountsLoading(true);
    setResultsError(null);
    setRegionCounts(new Map());

    const useRegionalPagination =
      selectedCategorySlugs.length === 0 ||
      viewMode === "categories" ||
      isLeafCategory;

    const pageState = useRegionalPagination ? regionalPage : thematicPage;
    const perPageState = useRegionalPagination ? regionalPerPage : thematicPerPage;
    const safeSize = Math.max(1, Math.min(100, parseInt(perPageState, 10) || 10));
    const backendPage = Math.max(0, pageState - 1);
    const effectiveQuery = (committedQueryDe ?? committedQuery ?? "").trim();
    const requestKey = [
      effectiveQuery,
      selectedRegions.join(","),
      selectedCategorySlugs.join("/"),
      String(backendPage),
      String(safeSize),
      sortBy,
      filterDate,
    ].join("|");

    searchWebsitesPaged(
      {
        query: effectiveQuery,
        regionKeys: selectedRegions,
        categorySlugs: selectedCategorySlugs,
        page: backendPage,
        size: safeSize,
        sortBy,
        dateFilter: filterDate,
      },
      {
        signal: controller.signal,
      }
    )
      .then((paged) => {
        if (!active) return;
        resultsAutoRetryRef.current.delete(requestKey);

        setResults(paged.items ?? []);
        setResultsTotalElements(paged.totalElements ?? 0);
        setResultsTotalPages(paged.totalPages ?? 0);
        setResolvedResultsKey(resultsSyncKey);
        setLoadingResults(false);

        // Clamp current page if totals changed due filter/sort updates.
        if (paged.totalPages > 0 && pageState > paged.totalPages) {
          if (useRegionalPagination) {
            setRegionalPage(paged.totalPages);
          } else {
            setThematicPage(paged.totalPages);
          }
        }

        return Promise.allSettled([
          fetchThematicCategoryCounts(selectedRegions, {
            query: effectiveQuery,
            dateFilter: filterDate,
            signal: controller.signal,
          }),
          fetchRegionCounts(
            {
              layer: mapLayer,
              query: effectiveQuery,
              categorySlugs: selectedCategorySlugs,
              state: mapActiveState,
              dateFilter: filterDate,
            },
            { signal: controller.signal }
          ),
        ]);
      })
      .then((facetResults) => {
        if (!active || !facetResults) return;

        const [thematicResult, regionResult] = facetResults;

        if (thematicResult.status === "fulfilled") {
          setCategoryCountsMap(
            flattenThematicCounts(
              Array.isArray(thematicResult.value) ? thematicResult.value : []
            )
          );
          setHasLoadedThematicCountsOnce(true);
        } else if (!isRequestCanceled(thematicResult.reason)) {
          console.error("fetchThematicCategoryCounts failed", thematicResult.reason);
        }

        if (regionResult.status === "fulfilled") {
          const nextRegionCounts = new Map<string, number>();
          (Array.isArray(regionResult.value) ? regionResult.value : []).forEach(
            (entry) => {
              if (!entry?.regionKey) return;
              const count =
                typeof entry.count === "number" && Number.isFinite(entry.count)
                  ? entry.count
                  : 0;
              nextRegionCounts.set(entry.regionKey, count);
            }
          );
          setRegionCounts(nextRegionCounts);
        } else if (!isRequestCanceled(regionResult.reason)) {
          console.error("fetchRegionCounts failed", regionResult.reason);
        }
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (isRequestCanceled(err)) return;
        console.error("searchWebsitesPaged failed", err);
        const message =
          err instanceof Error && err.message
            ? err.message
            : "Search request failed.";
        setResultsError(message);

        const retryCount = resultsAutoRetryRef.current.get(requestKey) ?? 0;
        if (retryCount < 1) {
          resultsAutoRetryRef.current.set(requestKey, retryCount + 1);
          retryTimer = setTimeout(() => {
            if (!active) return;
            setSearchSeq((x) => x + 1);
          }, 700);
        }
      })
      .finally(() => {
        if (!active) return;
        setLoadingResults(false);
        setThematicCountsLoading(false);
      });

    return () => {
      active = false;
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
      controller.abort();
    };
  }, [
    committedQuery,
    committedQueryDe,
    searchSeq,
    selectedRegions,
    selectedCategorySlugs,
    regionalPage,
    regionalPerPage,
    thematicPage,
    thematicPerPage,
    sortBy,
    filterDate,
    viewMode,
    isLeafCategory,
    mapLayer,
    mapActiveState,
    resultsSyncKey,
  ]);

  const handleCategoryClick = (categorySlug: string) => {
    if (selectedCategorySlugs.length >= MAX_CATEGORY_PATH_SEGMENTS) {
      return;
    }
    setSelectedCategorySlugs((p) => [...p, categorySlug]);
    setViewMode("categories");
    setThematicPage(1);
    setRegionalPage(1);
  };

  const handleCategoryPathSelect = useCallback(
    (categoryPathSlugs: string[]) => {
      const limitedPath = categoryPathSlugs.slice(0, MAX_CATEGORY_PATH_SEGMENTS);
      const normalizedPath = normalizeSelectedCategoryPath(
        thematicCategories,
        limitedPath
      );
      setSelectedCategorySlugs(normalizedPath);
      setViewMode("categories");
      setThematicPage(1);
      setRegionalPage(1);
    },
    [thematicCategories]
  );

  const handleBreadcrumbClick = (index: number) => {
    if (index === -1) {
      setSelectedCategorySlugs([]);
      setViewMode("categories");
    } else {
      setSelectedCategorySlugs((p) => p.slice(0, index + 1));
      setViewMode("categories");
    }
    setThematicPage(1);
    setRegionalPage(1);
  };

  // --- Regional selection changes reset pagination ---
  const handleRegionalSelectionChange = useCallback((regionKeys: string[]) => {
    const normalizedRegionKeys = normalizeRegionKeys(regionKeys);
    if (regionKeySignature(selectedRegions) === regionKeySignature(normalizedRegionKeys)) {
      return;
    }
    setSelectedRegions(normalizedRegionKeys);
    setRegionalPage(1);
    setThematicPage(1);
  }, [selectedRegions]);

  const handleMapStateChange = useCallback(
    (layer: MapLayerId, state: string | null) => {
      setMapLayer(layer);
      setMapActiveState(state);
      setSelectedRegions((previousRegions) => {
        const constrainedRegions = constrainRegionKeysToMapContext(
          previousRegions,
          layer,
          state
        );
        if (
          regionKeySignature(previousRegions) ===
          regionKeySignature(constrainedRegions)
        ) {
          return previousRegions;
        }
        return constrainedRegions;
      });
    },
    []
  );

  const regionalPerPageNum = Math.max(1, parseInt(regionalPerPage, 10) || 10);
  const thematicPerPageNum = Math.max(1, parseInt(thematicPerPage, 10) || 10);

  const useRegionalPagination =
    selectedCategorySlugs.length === 0 ||
    viewMode === "categories" ||
    isLeafCategory;
  const activePageForUrl = useRegionalPagination ? regionalPage : thematicPage;
  const activePerPageForUrl = useRegionalPagination
    ? regionalPerPage
    : thematicPerPage;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const normalizedRegions = normalizeRegionKeys(selectedRegions);
    const normalizedCategorySlugs = selectedCategorySlugs
      .map((slug) => normalizeCategorySlug(slug))
      .filter((slug): slug is string => !!slug)
      .slice(0, MAX_CATEGORY_PATH_SEGMENTS);
    const normalizedSize = PER_PAGE_VALUES.has(activePerPageForUrl)
      ? activePerPageForUrl
      : DEFAULT_PER_PAGE;
    const nextQueryString = buildSearchPageQueryString({
      query: normalizeQueryParam(committedQuery ?? ""),
      queryDe: normalizeQueryParam(committedQueryDe ?? ""),
      sortBy: SORT_BY_VALUES.has(sortBy) ? sortBy : DEFAULT_SORT_BY,
      dateFilter: DATE_FILTER_VALUES.has(filterDate)
        ? filterDate
        : DEFAULT_DATE_FILTER,
      page: clampUiPage(activePageForUrl),
      size: normalizedSize,
      regions: normalizedRegions,
      categorySlugs: normalizedCategorySlugs,
    });
    const nextUrl = nextQueryString
      ? `${window.location.pathname}?${nextQueryString}`
      : window.location.pathname;
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (nextUrl === currentUrl) {
      return;
    }

    const state =
      window.history.state && typeof window.history.state === "object"
        ? window.history.state
        : {};
    window.history.replaceState(state, "", nextUrl);
  }, [
    committedQuery,
    committedQueryDe,
    sortBy,
    filterDate,
    selectedRegions,
    selectedCategorySlugs,
    activePageForUrl,
    activePerPageForUrl,
  ]);

  const activePerPageNum = useRegionalPagination
    ? regionalPerPageNum
    : thematicPerPageNum;
  const activeTotalPages = Math.max(
    1,
    resultsTotalPages > 0
      ? resultsTotalPages
      : Math.ceil(resultsTotalElements / activePerPageNum) || 1
  );

  const regionalResultsCount = resultsTotalElements;
  const thematicResultsCount = resultsTotalElements;

  const regionalTotalPages = useRegionalPagination
    ? activeTotalPages
    : Math.max(1, Math.ceil(regionalResultsCount / regionalPerPageNum) || 1);
  const thematicTotalPages = useRegionalPagination
    ? Math.max(1, Math.ceil(thematicResultsCount / thematicPerPageNum) || 1)
    : activeTotalPages;

  const regionalSlice = results;
  const thematicSlice = results;

  const regionalRangeStart =
    regionalResultsCount === 0 ? 0 : (regionalPage - 1) * regionalPerPageNum + 1;
  const thematicRangeStart =
    thematicResultsCount === 0 ? 0 : (thematicPage - 1) * thematicPerPageNum + 1;

  const regionalRangeEnd =
    regionalSlice.length === 0
      ? regionalRangeStart
      : regionalRangeStart + regionalSlice.length - 1;
  const thematicRangeEnd =
    thematicSlice.length === 0
      ? thematicRangeStart
      : thematicRangeStart + thematicSlice.length - 1;

  const handleViewResults = useCallback(() => {
    scrollToResultsHint();
  }, [scrollToResultsHint]);

  const topPaginationContainerRef = useRef<HTMLDivElement | null>(null);
  const bottomPaginationContainerRef = useRef<HTMLDivElement | null>(null);
  const previousButtonRef = useRef<HTMLButtonElement | null>(null);
  const nextButtonRef = useRef<HTMLButtonElement | null>(null);
  const paginationNumberMeasureRef = useRef<HTMLButtonElement | null>(null);

  const resolveEffectiveWidth = useCallback(() => {
    if (typeof window === "undefined") return null;
    const factor = toUiScaleFactor(uiScale);
    if (!Number.isFinite(factor) || factor <= 0) {
      return window.innerWidth;
    }
    return window.innerWidth / factor;
  }, [uiScale]);

  const resolveMaxPaginationButtonsByFit = useCallback(() => {
    const topWidth = topPaginationContainerRef.current?.clientWidth ?? 0;
    const bottomWidth = bottomPaginationContainerRef.current?.clientWidth ?? 0;
    const measuredWidths = [topWidth, bottomWidth].filter((width) => width > 0);
    const measuredContainerWidth =
      measuredWidths.length > 0
        ? Math.min(...measuredWidths)
        : Math.max(topWidth, bottomWidth);
    if (measuredContainerWidth <= 0) {
      return MAX_PAGINATION_BUTTONS;
    }

    const previousWidth = previousButtonRef.current?.offsetWidth ?? 40;
    const nextWidth = nextButtonRef.current?.offsetWidth ?? 40;
    const numberButtonWidth = paginationNumberMeasureRef.current?.offsetWidth ?? 36;

    const reservedWidth =
      previousWidth + nextWidth + PAGINATION_OUTER_GAP_PX * 2;
    const availableWidth = Math.max(0, measuredContainerWidth - reservedWidth);

    const fitCount = Math.floor(
      (availableWidth + PAGINATION_INNER_GAP_PX) /
        (numberButtonWidth + PAGINATION_INNER_GAP_PX)
    );

    return Math.max(
      MIN_PAGINATION_BUTTONS,
      Math.min(MAX_PAGINATION_BUTTONS, fitCount)
    );
  }, []);

  const [maxPaginationButtons, setMaxPaginationButtons] = useState(
    MAX_PAGINATION_BUTTONS
  );

  const refreshMaxPaginationButtons = useCallback(() => {
    const nextCount = resolveMaxPaginationButtonsByFit();
    setMaxPaginationButtons((currentCount) =>
      currentCount === nextCount ? currentCount : nextCount
    );
  }, [resolveMaxPaginationButtonsByFit]);

  const resolveStackRegionalBelow = useCallback(() => {
    const width = resolveEffectiveWidth();
    if (width === null) {
      return false;
    }
    return width < TABLET_LAYOUT_BREAKPOINT;
  }, [resolveEffectiveWidth]);

  const [stackRegionalBelow, setStackRegionalBelow] = useState(() =>
    resolveStackRegionalBelow()
  );

  useEffect(() => {
    refreshMaxPaginationButtons();
  }, [
    refreshMaxPaginationButtons,
    regionalPage,
    thematicPage,
    resultsTotalPages,
  ]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => {
      refreshMaxPaginationButtons();
    };
    update();

    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(update)
        : null;

    if (observer) {
      if (topPaginationContainerRef.current) {
        observer.observe(topPaginationContainerRef.current);
      }
      if (bottomPaginationContainerRef.current) {
        observer.observe(bottomPaginationContainerRef.current);
      }
      if (previousButtonRef.current) {
        observer.observe(previousButtonRef.current);
      }
      if (nextButtonRef.current) {
        observer.observe(nextButtonRef.current);
      }
      if (paginationNumberMeasureRef.current) {
        observer.observe(paginationNumberMeasureRef.current);
      }
    }

    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      observer?.disconnect();
    };
  }, [refreshMaxPaginationButtons]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const compute = () => {
      setStackRegionalBelow(resolveStackRegionalBelow());
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [resolveStackRegionalBelow]);

  useEffect(() => {
    if (stackRegionalBelow) {
      setThematicSectionMaxHeightPx(null);
      return;
    }

    const node = regionalFilterRef.current;
    if (!node) return;

    const updateHeight = () => {
      const height = Math.floor(node.getBoundingClientRect().height);
      setThematicSectionMaxHeightPx(height > 0 ? height : null);
    };

    updateHeight();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateHeight);
    observer.observe(node);
    return () => observer.disconnect();
  }, [stackRegionalBelow, viewMode, selectedCategorySlugs, isLeafCategory]);

  const isRootLevel = selectedCategorySlugs.length === 0;
  const showThematicFilters = true;
  const thematicInitialLoading =
    thematicTreeLoading || (!hasLoadedThematicCountsOnce && thematicCountsLoading);

  const showingSubcategories = !isRootLevel && viewMode === "categories";
  const currentSubcategories = showingSubcategories
    ? currentCategory?.subcategories ?? []
    : [];

  const resetPaginationToFirstPage = useCallback(() => {
    setRegionalPage(1);
    setThematicPage(1);
  }, []);

  const removeQueryFilter = useCallback(() => {
    setSharedQuery("");
    setCommittedQuery(null);
    setCommittedQueryDe(null);
    setSearchError(null);
    resetPaginationToFirstPage();
  }, [resetPaginationToFirstPage, setSharedQuery]);

  const removeCategoryFilter = useCallback(() => {
    setSelectedCategorySlugs([]);
    resetPaginationToFirstPage();
  }, [resetPaginationToFirstPage]);

  const selectCategoryPathDepth = useCallback(
    (depth: number) => {
      setSelectedCategorySlugs((previousSlugs) =>
        previousSlugs.slice(0, Math.max(1, depth + 1))
      );
      resetPaginationToFirstPage();
    },
    [resetPaginationToFirstPage]
  );

  const removeRegionFilter = useCallback(
    (regionKey: string) => {
      setSelectedRegions((previousRegions) =>
        normalizeRegionKeys(
          previousRegions.filter((currentRegionKey) => currentRegionKey !== regionKey)
        )
      );
      resetPaginationToFirstPage();
    },
    [resetPaginationToFirstPage]
  );

  const clearAllFilters = useCallback(() => {
    setSharedQuery("");
    setCommittedQuery(null);
    setCommittedQueryDe(null);
    setSearchError(null);
    setSelectedCategorySlugs([]);
    setSelectedRegions([]);
    resetPaginationToFirstPage();
  }, [resetPaginationToFirstPage, setSharedQuery]);

  const formatRegionFilterLabel = useCallback(
    (regionKey: string) => {
      const catalogLabel = regionCatalogLabels[regionKey];
      if (catalogLabel) {
        return catalogLabel;
      }
      const parsed = parseRegionSelectionKey(regionKey);
      if (!parsed) {
        return regionKey;
      }
      if (parsed.layerId === "federalStates") {
        return parsed.state;
      }
      if (parsed.layerId === "postalCodes") {
        const postalCodeLabel =
          getPostalCodeDisplayLabel(parsed.regionName) ?? parsed.regionName;
        return `${postalCodeLabel} (${parsed.state})`;
      }
      return `${parsed.regionName} (${parsed.state})`;
    },
    [regionCatalogLabels]
  );

  type ActiveFilterChip = {
    id: string;
    kind: "query" | "category" | "region";
    label?: string;
    regionKey?: string;
    pathSlugs?: string[];
    pathLabels?: string[];
  };

  const activeFilterChips = useMemo<ActiveFilterChip[]>(() => {
    const chips: ActiveFilterChip[] = [];
    const trimmedQuery = (committedQuery ?? "").trim();
    if (trimmedQuery.length > 0) {
      chips.push({
        id: "query",
        kind: "query",
        label: trimmedQuery,
      });
    }

    if (selectedCategorySlugs.length > 0) {
      const categoryPathLabels =
        categoryPath.length === selectedCategorySlugs.length
          ? categoryPath
          : selectedCategorySlugs;
      chips.push({
        id: "category",
        kind: "category",
        pathSlugs: [...selectedCategorySlugs],
        pathLabels: categoryPathLabels,
      });
    }

    selectedRegions.forEach((regionKey) => {
      chips.push({
        id: `region:${regionKey}`,
        kind: "region",
        regionKey,
        label: formatRegionFilterLabel(regionKey),
      });
    });

    return chips;
  }, [
    categoryPath,
    committedQuery,
    formatRegionFilterLabel,
    selectedCategorySlugs,
    selectedRegions,
    t,
  ]);

  const hasActiveFilters = activeFilterChips.length > 0;

  const TopPaginationControls = () => (
    <div
      ref={topPaginationContainerRef}
      className="flex justify-center items-center gap-2 w-full"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={previousButtonRef}
            variant="outline"
            size="icon"
            onClick={() => setRegionalPage((p) => Math.max(1, p - 1))}
            disabled={regionalPage === 1}
            aria-label={t("categories.paginationPrevious")}
          >
            <ChevronLeft className="w-4 h-4" style={iconStyle} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{t("categories.paginationPrevious")}</TooltipContent>
      </Tooltip>

      <div className="flex items-center gap-1 justify-center">
        {renderPagination(
          regionalPage,
          regionalTotalPages,
          (page) => setRegionalPage(page),
          maxPaginationButtons,
          paginationNumberMeasureRef
        )}
      </div>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={nextButtonRef}
            variant="outline"
            size="icon"
            onClick={() =>
              setRegionalPage((p) => Math.min(regionalTotalPages, p + 1))
            }
            disabled={regionalPage === regionalTotalPages}
            aria-label={t("common.next")}
          >
            <ChevronRight className="w-4 h-4" style={iconStyle} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{t("common.next")}</TooltipContent>
      </Tooltip>
    </div>
  );

  const BottomPaginationControls = () => (
    <div
      ref={bottomPaginationContainerRef}
      className="flex justify-center items-center gap-2 mt-2 w-full"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={previousButtonRef}
            variant="outline"
            size="icon"
            onClick={() => setThematicPage((p) => Math.max(1, p - 1))}
            disabled={thematicPage === 1}
            aria-label={t("categories.paginationPrevious")}
          >
            <ChevronLeft className="w-4 h-4" style={iconStyle} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{t("categories.paginationPrevious")}</TooltipContent>
      </Tooltip>

      <div className="flex items-center gap-1 justify-center">
        {renderPagination(
          thematicPage,
          thematicTotalPages,
          (page) => setThematicPage(page),
          maxPaginationButtons,
          paginationNumberMeasureRef
        )}
      </div>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={nextButtonRef}
            variant="outline"
            size="icon"
            onClick={() =>
              setThematicPage((p) => Math.min(thematicTotalPages, p + 1))
            }
            disabled={thematicPage === thematicTotalPages}
            aria-label={t("common.next")}
          >
            <ChevronRight className="w-4 h-4" style={iconStyle} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{t("common.next")}</TooltipContent>
      </Tooltip>
    </div>
  );

  const ResultsFilterBar: React.FC = () => {
    const isThematicView = !showThematicFilters;
    const filterInputId = "search-page-query-input";

    const perPage = isThematicView ? thematicPerPage : regionalPerPage;
    const setPerPage = isThematicView ? setThematicPerPage : setRegionalPerPage;

    const resetToPage1 = isThematicView
      ? () => setThematicPage(1)
      : () => setRegionalPage(1);

    return (
      <div className="space-y-4">
        <Card className="stat-card gap-1.5">
          <CardHeader className="pb-0">
            <CardTitle
              className="flex items-center gap-2 card-title-text"
              style={primaryTextStyle}
            >
              <SearchIcon className="w-5 h-5 icon-primary" style={iconStyle} />
              {t("categories.searchTermPlaceholder")}
            </CardTitle>
          </CardHeader>

          <CardContent className="pt-0">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmitSearch();
              }}
            >
              <label htmlFor={filterInputId} className="sr-only">
                {t("common.searchPlaceholder")}
              </label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <div className="relative flex-1">
                  <SearchIcon
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
                    style={iconStyle}
                  />
                  <Input
                    id={filterInputId}
                    name="searchQuery"
                    ref={filterInputRef}
                    value={sharedQuery ?? ""}
                    onChange={(e) => {
                      setSharedQuery(e.target.value);
                      setCommittedQueryDe(null);
                      setSearchError(null);
                      resetToPage1();
                    }}
                    onKeyDown={(e) => {
                      const native = e.nativeEvent as any;
                      if (e.key === "Enter" && !native?.isComposing) {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmitSearch();
                      }
                    }}
                    onFocus={() => {
                      filterInputFocusedRef.current = true;
                    }}
                    onBlur={() => {
                      filterInputFocusedRef.current = false;
                    }}
                    maxLength={MAX_SEARCH_QUERY_LENGTH}
                    aria-invalid={!!searchError}
                    placeholder={t("common.searchPlaceholder")}
                    className="pl-10 input-standard"
                  />
                </div>
                <Button type="submit" className="gap-2 sm:min-w-28">
                  <SearchIcon className="w-4 h-4" />
                  {t("common.search")}
                </Button>
              </div>
            </form>
            {/* show validation message */}
            {searchError && (
              <p
                className="mt-2 text-sm text-red-600"
                role="alert"
                style={errorTextStyle}
              >
                {searchError}
              </p>
            )}
          </CardContent>
        </Card>

        <Card className="stat-card gap-1.5">
          <CardHeader className="pb-0">
            <div className="flex items-center justify-between">
              <CardTitle
                className="flex items-center gap-2 card-title-text"
                style={primaryTextStyle}
              >
                <Filter className="w-5 h-5 icon-primary" style={iconStyle} />
                {t("categories.filterSort")}
              </CardTitle>
            </div>
          </CardHeader>

          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label
                  className="text-sm text-secondary"
                  style={secondaryTextStyle}
                >
                  {t("categories.sortBy")}
                </label>

                <Select
                  value={sortBy}
                  onValueChange={(v) => {
                    setSortBy(v);
                    resetToPage1();
                  }}
                >
                  <SelectTrigger className="input-standard">
                    <SelectValue
                      placeholder={t("categories.sortBy")}
                      className="select-placeholder"
                    />
                  </SelectTrigger>

                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    <SelectItem value="relevance">
                      {t("categories.sortRelevance")}
                    </SelectItem>
                    <SelectItem value="date-new">
                      {t("categories.sortDateNew")}
                    </SelectItem>
                    <SelectItem value="date-old">
                      {t("categories.sortDateOld")}
                    </SelectItem>
                    <SelectItem value="title-asc">
                      {t("categories.sortTitleAsc")}
                    </SelectItem>
                    <SelectItem value="title-desc">
                      {t("categories.sortTitleDesc")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label
                  className="text-sm text-secondary"
                  style={secondaryTextStyle}
                >
                  {t("categories.filterTime")}
                </label>

                <Select
                  value={filterDate}
                  onValueChange={(v) => {
                    setFilterDate(v);
                    resetToPage1();
                  }}
                >
                  <SelectTrigger className="input-standard">
                    <SelectValue
                      placeholder={t("categories.filterTime")}
                      className="select-placeholder"
                    />
                  </SelectTrigger>

                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    <SelectItem value="all">
                      {t("categories.allPeriods")}
                    </SelectItem>
                    <SelectItem value="week">
                      {t("categories.lastWeek")}
                    </SelectItem>
                    <SelectItem value="month">
                      {t("categories.lastMonth")}
                    </SelectItem>
                    <SelectItem value="year">
                      {t("categories.lastYear")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label
                  className="text-sm text-secondary"
                  style={secondaryTextStyle}
                >
                  {t("categories.resultsPerPage")}
                </label>

                <Select
                  value={perPage}
                  onValueChange={(v) => {
                    setPerPage(v);
                    resetToPage1();
                  }}
                >
                  <SelectTrigger className="input-standard">
                    <SelectValue
                      placeholder="10"
                      className="select-placeholder"
                    />
                  </SelectTrigger>

                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-2 pt-1">
                <div className="flex flex-col gap-2">
                  <div className="min-w-0 flex flex-wrap items-center gap-2">
                    <span className="text-sm font-medium" style={secondaryTextStyle}>
                      {t("categories.activeFiltersTitle")}
                    </span>
                    <div className="min-w-0 flex flex-wrap items-center gap-2">
                      {activeFilterChips.map((chip) => {
                        if (chip.kind === "category") {
                          const pathLabels = chip.pathLabels ?? [];
                          const pathSlugs = chip.pathSlugs ?? [];
                          const categoryLabel = t("categories.activeCategoryLabel");
                          const removeLabel = `${t("categories.removeFilter")}: ${categoryLabel}`;
                          return (
                            <Badge
                              key={chip.id}
                              variant="secondary"
                              className="min-w-0 max-w-full shrink w-fit flex items-start justify-start gap-1 px-3 py-1 rounded-full whitespace-normal break-words overflow-visible"
                            >
                              <span className="min-w-0 flex flex-wrap items-center gap-x-1 gap-y-0.5 text-xs font-medium whitespace-normal break-words [overflow-wrap:anywhere]">
                                <span className="shrink-0 text-xs font-medium">
                                  {categoryLabel}:
                                </span>
                                {pathLabels.map((label, index) => (
                                  <React.Fragment key={`${pathSlugs[index] ?? label}-${index}`}>
                                    {index > 0 && (
                                      <span className="shrink-0 text-[10px] opacity-60">&gt;</span>
                                    )}
                                    {index < pathLabels.length - 1 ? (
                                      <button
                                        type="button"
                                        onClick={() => selectCategoryPathDepth(index)}
                                        className="min-w-0 max-w-full text-left text-xs font-medium underline decoration-dotted underline-offset-2 hover:text-[var(--primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-sm whitespace-normal break-words [overflow-wrap:anywhere]"
                                        title={`${t("categories.activeCategoryLabel")}: ${label}`}
                                        aria-label={`${t("categories.activeCategoryLabel")}: ${label}`}
                                      >
                                        {label}
                                      </button>
                                    ) : (
                                      <span
                                        className="min-w-0 max-w-full text-xs font-medium whitespace-normal break-words [overflow-wrap:anywhere]"
                                        title={`${t("categories.activeCategoryLabel")}: ${label}`}
                                      >
                                        {label}
                                      </span>
                                    )}
                                  </React.Fragment>
                                ))}
                              </span>
                              <button
                                type="button"
                                onClick={removeCategoryFilter}
                                className="inline-flex shrink-0 self-start items-center justify-center rounded-full p-0.5 hover:bg-slate-300/50 dark:hover:bg-slate-700/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                                aria-label={removeLabel}
                                title={removeLabel}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </Badge>
                          );
                        }

                        const chipLabel =
                          chip.kind === "query"
                            ? `${t("categories.activeQueryLabel")}: "${chip.label ?? ""}"`
                            : chip.label ?? "";
                        const removeLabel = `${t("categories.removeFilter")}: ${chipLabel}`;

                        return (
                          <Badge
                            key={chip.id}
                            variant="secondary"
                            className="min-w-0 max-w-full shrink w-fit flex items-start justify-start gap-1 px-3 py-1 rounded-full whitespace-normal break-words overflow-visible"
                          >
                            <span className="min-w-0 text-xs font-medium whitespace-normal break-words [overflow-wrap:anywhere]">
                              {chipLabel}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                if (chip.kind === "query") {
                                  removeQueryFilter();
                                  return;
                                }
                                if (chip.regionKey) {
                                  removeRegionFilter(chip.regionKey);
                                }
                              }}
                              className="inline-flex shrink-0 self-start items-center justify-center rounded-full p-0.5 hover:bg-slate-300/50 dark:hover:bg-slate-700/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                              aria-label={removeLabel}
                              title={removeLabel}
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearAllFilters}
                    className="self-start gap-1 h-7 px-2 text-xs"
                  >
                    <X className="w-4 h-4" />
                    {t("categories.clearAllFilters")}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderWebsiteCard = (site: WebsiteSearchResult) => {
    const hostnameLabel = site.hostname
      ? site.hostname
      : site.urlLandingPage
      ? site.urlLandingPage.replace(/^https?:\/\//, "")
      : "";
    const addressLabel = [site.zipCode, site.address].filter(Boolean).join(" ");
    const detailLabel = t("websiteDetails.openDetails");

    return (
      <Card
        key={site.id}
        className="stat-card table-row-hover transition-all"
      >
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex-1">
              {site.urlLandingPage ? (
                <a
                  href={site.urlLandingPage}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => {
                    event.stopPropagation();
                    recordPageview(site.id);
                  }}
                  className="text-primary-heading mb-2 flex items-center gap-2 text-[var(--search-link)] hover:text-[var(--search-link-hover)] cursor-pointer"
                  style={searchLinkStyle}
                >
                  {site.title}
                  <ExternalLink className="w-4 h-4" style={iconStyle} />
                </a>
              ) : (
                <div className="text-primary-heading mb-2 flex items-center gap-2">
                  {site.title}
                </div>
              )}

              <p className="text-secondary mb-3" style={secondaryTextStyle}>
                {site.description}
              </p>

              <div
                className="flex flex-wrap gap-3 text-sm text-tertiary"
                style={secondaryTextStyle}
              >
                {hostnameLabel && (
                  <span className="flex items-center gap-1">
                    <Globe className="w-4 h-4" style={iconStyle} />
                    {hostnameLabel}
                  </span>
                )}

                {addressLabel && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" style={iconStyle} />
                    {addressLabel}
                  </span>
                )}

                {site.pageviewsTotal != null && (
                  <span className="flex items-center gap-1">
                    <Eye className="w-4 h-4" style={iconStyle} />
                    {numberFormatter.format(site.pageviewsTotal)}
                  </span>
                )}
              </div>
            </div>

            <Button
              variant="outline"
              size="icon"
              title={detailLabel}
              aria-label={detailLabel}
              onClick={(event) => {
                event.stopPropagation();
                openDetail(site.id);
              }}
            >
              <Info className="w-4 h-4" style={iconStyle} />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderResultSkeletons = (count: number) => (
    <div className="space-y-4">
      {Array.from({ length: Math.max(1, count) }).map((_, idx) => (
        <Card key={`result-skeleton-${idx}`} className="stat-card">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex-1 space-y-3">
                <Skeleton className="h-6 w-2/3" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <div className="flex flex-wrap gap-3">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-20" />
                </div>
              </div>
              <Skeleton className="h-9 w-9 rounded-md" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="search-page-shell flex flex-col min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header
        navigate={navigate}
        currentUser={currentUser}
        onLogout={onLogout}
        currentPage={pageKey}
        samePageHandlers={{
          categories: focusCategoriesSection,
          search: focusSearchSection,
        }}
        onNavigateToSearchPage={onNavigateToSearchPage}
      />

      <main className="flex-1 py-1 pb-12">
        <div className="container mx-auto px-4 pt-4">
          {showThematicFilters && (
            <div>
              <div className="mb-4">
                <HintBanner
                  ref={categoryHintRef}
                  title={t("categories.scrollDownHintTitle")}
                  description={t("categories.scrollDownHintDescription")}
                  icon={<ChevronsDown className="w-5 h-5" style={iconStyle} />}
                  onActivate={scrollToResultsHint}
                />
              </div>

              <div className="w-full flex flex-col gap-4">
                <div
                  className={
                    stackRegionalBelow ? "grid gap-4" : "grid gap-4 lg:grid-cols-2"
                  }
                >
                  <ThematicFilterSection
                    categoryPath={categoryPath}
                    currentCategory={currentCategory}
                    currentSubcategories={currentSubcategories}
                    showingSubcategories={showingSubcategories}
                    thematicCategories={thematicCategories}
                    isLoading={thematicInitialLoading}
                    handleCategoryClick={handleCategoryClick}
                    handleCategoryPathSelect={handleCategoryPathSelect}
                    handleBreadcrumbClick={handleBreadcrumbClick}
                    handleViewResults={handleViewResults}
                    sectionMaxHeightPx={
                      stackRegionalBelow ? null : thematicSectionMaxHeightPx
                    }
                    t={t}
                  />

                  <div ref={regionalFilterRef}>
                    <RegionalFilterSection
                      handleViewResults={handleViewResults}
                      selectedRegionKeys={selectedRegions}
                      onSelectionChange={handleRegionalSelectionChange}
                      onMapStateChange={handleMapStateChange}
                      regionCounts={regionCounts}
                      language={language === "en" ? "en" : "de"}
                      t={t}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <HintBanner
                    ref={resultsHintRef}
                    title={t("categories.scrollUpHintTitle")}
                    description={t("categories.scrollUpHintDescription")}
                    icon={<ChevronsUp className="w-5 h-5" style={iconStyle} />}
                    onActivate={scrollToCategoryHint}
                  />

                  <div
                    ref={resultsFilterRef}
                    tabIndex={-1}
                    className="outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
                  >
                    <ResultsFilterBar />
                  </div>

                </div>

                <div className="space-y-4">
                  {resultsError && (
                    <Card>
                    <CardContent
                      className="py-6 text-center text-red-600"
                      style={errorTextStyle}
                    >
                      {resultsError}
                    </CardContent>
                    </Card>
                  )}

                  {TopPaginationControls()}

                  <Card className="stat-card">
                    <CardHeader>
                    <CardTitle
                      className="card-title-text flex items-center gap-2"
                      style={primaryTextStyle}
                    >
                      <SearchIcon className="w-5 h-5 icon-primary" style={iconStyle} />
                      {t("categories.searchResultsTitle")}
                    </CardTitle>
                    {loadingResults ? (
                      <div className="pb-3">
                        <Skeleton className="h-4 w-56" />
                      </div>
                    ) : (
                      <CardDescription
                        className="text-secondary pb-3"
                        style={secondaryTextStyle}
                      >
                        {regionalResultsCount === 0
                            ? t("common.noResults")
                            : formatResultsSummary(
                                regionalRangeStart,
                                regionalRangeEnd,
                                regionalResultsCount
                              )}
                      </CardDescription>
                    )}
                    </CardHeader>
                  </Card>

                  {loadingResults ? (
                    renderResultSkeletons(regionalPerPageNum)
                  ) : (
                    <div className="space-y-4">
                      {regionalSlice.map((site) => renderWebsiteCard(site))}

                      {regionalSlice.length === 0 && (
                        <Card>
                          <CardContent
                            className="py-10 text-center text-secondary"
                            style={secondaryTextStyle}
                          >
                            {t("common.noResults")}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {TopPaginationControls()}
                </div>
              </div>
            </div>
          )}

          {!showThematicFilters && (
            <div>
              <div className="space-y-4">
                <HintBanner
                  ref={resultsHintRef}
                  title={t("categories.scrollUpHintTitle")}
                  description={t("categories.scrollUpHintDescription")}
                  icon={<ChevronsUp className="w-5 h-5" style={iconStyle} />}
                  onActivate={focusCategoriesSection}
                />

                <div
                  ref={resultsFilterRef}
                  tabIndex={-1}
                  className="outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-lg"
                >
                  <ResultsFilterBar />
                </div>

              </div>

              <div className="space-y-4">
                <Card className="stat-card">
                  <CardHeader>
                    <CardTitle
                      className="card-title-text flex items-center gap-2"
                      style={primaryTextStyle}
                    >
                      <SearchIcon className="w-5 h-5 icon-primary" style={iconStyle} />
                      {t("categories.searchResultsTitle")}
                    </CardTitle>

                    {loadingResults ? (
                      <div className="pb-3">
                        <Skeleton className="h-4 w-56" />
                      </div>
                    ) : (
                      <CardDescription
                        className="text-secondary pb-3"
                        style={secondaryTextStyle}
                      >
                        {thematicResultsCount === 0
                          ? t("common.noResults")
                          : formatResultsSummary(
                              thematicRangeStart,
                              thematicRangeEnd,
                              thematicResultsCount
                            )}
                      </CardDescription>
                    )}
                  </CardHeader>
                </Card>

                <div className="pt-2">{BottomPaginationControls()}</div>

                {loadingResults ? (
                  renderResultSkeletons(thematicPerPageNum)
                ) : (
                  <div className="space-y-4">
                    {thematicSlice.map((site) => renderWebsiteCard(site))}

                    {thematicSlice.length === 0 && (
                      <Card>
                        <CardContent
                          className="py-10 text-center text-secondary"
                          style={secondaryTextStyle}
                        >
                          {t("common.noResults")}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                {BottomPaginationControls()}
              </div>
            </div>
          )}
        </div>
        <div className="h-6" aria-hidden="true" />
      </main>

      <WebsiteDetailDrawer
        open={detailOpen}
        websiteId={detailWebsiteId}
        onOpenChange={setDetailOpen}
        onSelectWebsite={openDetail}
        navigate={navigate}
      />

      <Footer navigate={navigate} />
    </div>
  );
}

function renderPagination(
  currentPage: number,
  totalPages: number,
  setPage: (value: number) => void,
  maxVisible = 10,
  numberMeasureRef?: React.RefObject<HTMLButtonElement | null>
) {
  const safeTotalPages = Math.max(1, totalPages);
  const safeCurrentPage = Math.min(Math.max(1, currentPage), safeTotalPages);
  const visibleCount = Math.max(1, Math.min(maxVisible, safeTotalPages));
  const pages: number[] = [];

  if (safeTotalPages <= visibleCount) {
    for (let page = 1; page <= safeTotalPages; page += 1) {
      pages.push(page);
    }
  } else {
    const left = Math.floor((visibleCount - 1) / 2);
    const right = visibleCount - 1 - left;
    let start = safeCurrentPage - left;
    let end = safeCurrentPage + right;

    if (start < 1) {
      end += 1 - start;
      start = 1;
    }
    if (end > safeTotalPages) {
      start -= end - safeTotalPages;
      end = safeTotalPages;
    }

    start = Math.max(1, start);
    end = Math.min(safeTotalPages, end);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }
  }

  return pages.map((page, index) => (
    <Button
      key={page}
      ref={index === 0 ? numberMeasureRef : undefined}
      variant={currentPage === page ? "default" : "outline"}
      size="sm"
      onClick={() => setPage(page)}
      className={currentPage === page ? "bg-blue-600 hover:bg-blue-700" : ""}
    >
      {page}
    </Button>
  ));
}
